import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import { UserCheck, UserPlus, UserX, History, X, AlertCircle, Info, ChevronsRight } from "lucide-react";
import { api } from "@/lib/api";
import { TransitionsModal } from "@/components/TransitionsModal";
import { Section, Field, StatusBadge, formatDate } from "@/components/DetailLayout";
import { useRolesEfectivos, useSuplantacion } from "@/lib/sesion";

// ============================================================================
// Asignación de responsable interno por etapa (RF-03).
// Panel para el detalle de solicitud (Telecom/Seguridad): muestra el
// responsable de la etapa actual, permite asignar/reasignar (roles de
// reparto) y consulta el historial inmutable. Solo visible para internos.
// ============================================================================

interface AsignacionEtapa {
  estadoCodigo: string;
  estadoNombre: string | null;
  usuarioId: string;
  usuarioNombre: string;
  usuarioCorreo: string;
  origen: "AUTOMATICA" | "MANUAL";
  fechaAsignacion: string;
  asignadoPorNombre: string | null;
  reasignada: boolean;
}
interface AsignacionInstancia {
  instanciaId: string;
  sectorCodigo: string | null;
  estadoActualCodigo: string;
  estadoActualNombre: string | null;
  cerrada: boolean;
  actual: AsignacionEtapa | null;
  todas: AsignacionEtapa[];
}
interface HistorialItem {
  fecha: string;
  estadoCodigo: string;
  usuarioAnteriorNombre: string | null;
  usuarioNuevoNombre: string;
  asignadoPorNombre: string | null;
  origen: string;
  motivo: string | null;
}
interface UsuarioAsignable {
  id: string;
  nombreCompleto: string;
  correo: string;
}

/** Internos del OC: pueden ver el panel. Espejo del @PreAuthorize del backend. */
const ROLES_VER = new Set([
  "ADMIN_SICEN", "GERENTE_CALIDAD", "AUXILIAR_CALIDAD", "COORDINADOR_TECNICO",
  "EVALUADOR_TECNICO", "RESPONSABLE_TELECOM", "RESPONSABLE_ELECTRICA",
  "RESPONSABLE_EFICIENCIA", "RESPONSABLE_CERTIFICADOS", "FACTURACION",
  "AUDITOR_INTERNO", "DIRECCION", "VIGILANCIA",
]);
/** Roles con permiso de repartir carga (asignar/reasignar). */
const ROLES_ASIGNAR = new Set([
  "ADMIN_SICEN", "COORDINADOR_TECNICO", "RESPONSABLE_TELECOM",
  "RESPONSABLE_ELECTRICA", "RESPONSABLE_EFICIENCIA",
]);

/**
 * Sólo el correo del token. Los roles NO se leen de aquí: los decide
 * {@link useRolesEfectivos}, que es el único lugar que sabe si hay una
 * suplantación en curso.
 */
function correoDeAuth(token: string | undefined): string | null {
  if (!token) return null;
  try {
    return JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")))?.email ?? null;
  } catch {
    return null;
  }
}

export function AsignacionPanel({
  instanciaId,
  solicitudId,
  folio,
  sector,
}: {
  instanciaId: string | null;
  /** Con estos tres presentes, el panel ofrece «Avanzar solicitud» (TransitionsModal). */
  solicitudId?: string;
  folio?: string;
  sector?: "telecom" | "seguridad";
}) {
  const auth = useAuth();
  const qc = useQueryClient();
  const { suplantacion } = useSuplantacion();
  // Bajo suplantación manda la identidad efectiva: los roles y el correo del
  // usuario suplantado, porque de ellos depende qué asignaciones ve y hace.
  // Los roles los resuelve lib/sesion, que es el único lugar que decide cuáles rigen.
  const roles = useRolesEfectivos();
  const miCorreo = suplantacion.activa
    ? suplantacion.suplantadoCorreo
    : correoDeAuth(auth.user?.access_token);
  const puedeVer = roles.some((r) => ROLES_VER.has(r));
  const puedeAsignar = roles.some((r) => ROLES_ASIGNAR.has(r));

  const [modalOpen, setModalOpen] = useState(false);
  const [histOpen, setHistOpen] = useState(false);
  const [avanzarOpen, setAvanzarOpen] = useState(false);
  const [asignandome, setAsignandome] = useState(false);
  const [errorAsignarme, setErrorAsignarme] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["asignacion-instancia", instanciaId],
    queryFn: async () =>
      (await api.get<AsignacionInstancia>(`/api/asignaciones/instancias/${instanciaId}`)).data,
    enabled: !!instanciaId && puedeVer,
  });

  const qHist = useQuery({
    queryKey: ["asignacion-historial", instanciaId],
    queryFn: async () =>
      (await api.get<HistorialItem[]>(`/api/asignaciones/instancias/${instanciaId}/historial`)).data,
    enabled: !!instanciaId && puedeVer && histOpen,
  });

  if (!instanciaId || !puedeVer) return null;

  const a = q.data;
  const actual = a?.actual ?? null;

  // «Asignarme a mí» (mejora 2026-07): visible para cualquier interno mientras
  // la solicitud siga abierta y la etapa no esté ya asignada a este usuario.
  const yaEsMia =
    !!actual && !!miCorreo && actual.usuarioCorreo?.toLowerCase() === miCorreo.toLowerCase();
  const puedeAsignarme = !!a && !a.cerrada && !yaEsMia;

  const asignarme = async () => {
    if (!a) return;
    setAsignandome(true);
    setErrorAsignarme(null);
    try {
      await api.put(
        `/api/asignaciones/instancias/${a.instanciaId}/etapas/${a.estadoActualCodigo}/asignarme`,
      );
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["asignacion-instancia", instanciaId] }),
        qc.invalidateQueries({ queryKey: ["asignacion-historial", instanciaId] }),
      ]);
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string; title?: string } } }).response?.data?.detail ||
        (e as { response?: { data?: { title?: string } } }).response?.data?.title ||
        "No se pudo tomar la solicitud.";
      setErrorAsignarme(detail);
    } finally {
      setAsignandome(false);
    }
  };

  return (
    <Section title="Responsable interno (etapa actual)">
      <Field
        label="Asignación"
        span={3}
        value={
          q.isLoading ? (
            <span className="text-sm text-fg-subtle">Cargando…</span>
          ) : q.error || !a ? (
            <span className="text-sm text-fg-subtle">No se pudo cargar la asignación.</span>
          ) : (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                {actual ? (
                  <>
                    <StatusBadge tone="ok">
                      <UserCheck className="h-3 w-3" /> {actual.usuarioNombre}
                    </StatusBadge>
                    <span className="text-xs text-fg-muted">
                      etapa <span className="font-mono">{a.estadoActualCodigo}</span>
                      {" · desde "}{formatDate(actual.fechaAsignacion, true)}
                      {" · "}
                      {actual.origen === "AUTOMATICA"
                        ? "asignación automática (predeterminado de la etapa)"
                        : <>asignada por {actual.asignadoPorNombre ?? "—"}</>}
                    </span>
                    {actual.reasignada && (
                      <StatusBadge tone="warn">reasignada anteriormente</StatusBadge>
                    )}
                  </>
                ) : (
                  <>
                    <StatusBadge tone="warn">
                      <UserX className="h-3 w-3" /> Sin asignar
                    </StatusBadge>
                    <span className="text-xs text-fg-muted">
                      La etapa <span className="font-mono">{a.estadoActualCodigo}</span> no tiene
                      responsable{a.cerrada ? "" : " — configura el predeterminado o asigna manualmente"}.
                    </span>
                  </>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                {solicitudId && folio && sector && !a.cerrada && (
                  <button
                    type="button"
                    onClick={() => setAvanzarOpen(true)}
                    className="inline-flex cursor-pointer items-center gap-1 rounded bg-primary px-3 py-1.5 text-sm font-medium text-white transition hover:bg-primary-hover"
                  >
                    <ChevronsRight className="h-4 w-4" /> Avanzar solicitud
                  </button>
                )}
                {puedeAsignarme && (
                  <button
                    type="button"
                    onClick={asignarme}
                    disabled={asignandome}
                    className="inline-flex cursor-pointer items-center gap-1 rounded bg-estado-ok px-3 py-1.5 text-sm font-medium text-white transition hover:opacity-90 disabled:opacity-60"
                  >
                    <UserPlus className="h-4 w-4" />
                    {asignandome ? "Asignando…" : "Asignarme a mí"}
                  </button>
                )}
                {puedeAsignar && !a.cerrada && (
                  <button
                    type="button"
                    onClick={() => setModalOpen(true)}
                    className="inline-flex cursor-pointer items-center gap-1 rounded border border-primary-strong px-3 py-1.5 text-sm font-medium text-primary-strong transition hover:bg-primary-tint"
                  >
                    <UserCheck className="h-4 w-4" /> {actual ? "Reasignar" : "Asignar"} responsable
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setHistOpen((v) => !v)}
                  className="inline-flex cursor-pointer items-center gap-1 rounded border border-edge px-3 py-1.5 text-sm text-fg-muted transition hover:bg-surface-2"
                >
                  <History className="h-4 w-4" /> Historial de asignaciones
                </button>
              </div>

              {errorAsignarme && (
                <div className="flex items-start gap-2 rounded bg-estado-err-bg p-2 text-xs text-estado-err">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{errorAsignarme}</span>
                </div>
              )}

              {a.cerrada && (
                <p className="flex items-center gap-1.5 text-xs text-fg-subtle">
                  <Info className="h-3.5 w-3.5 shrink-0" />
                  La solicitud ya se encuentra concluida y no requiere asignación de responsable.
                </p>
              )}

              {histOpen && (
                <div className="rounded border">
                  <div className="border-b bg-surface-2 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-fg-subtle">
                    Historial ({qHist.data?.length ?? 0})
                  </div>
                  {qHist.isLoading ? (
                    <div className="p-3 text-sm text-fg-subtle">Cargando…</div>
                  ) : (qHist.data ?? []).length === 0 ? (
                    <div className="p-3 text-sm text-fg-subtle">
                      Esta solicitud se procesó sin asignaciones de responsable registradas.
                    </div>
                  ) : (
                    <ul className="divide-y">
                      {qHist.data!.map((h, i) => (
                        <li key={i} className="px-3 py-2 text-xs">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-fg-subtle">{formatDate(h.fecha, true)}</span>
                            <span className="font-mono text-fg-muted">{h.estadoCodigo}</span>
                            <span className="text-fg">
                              {h.usuarioAnteriorNombre
                                ? <>{h.usuarioAnteriorNombre} → <strong>{h.usuarioNuevoNombre}</strong></>
                                : <strong>{h.usuarioNuevoNombre}</strong>}
                            </span>
                            <span className="text-fg-subtle">
                              {h.origen === "AUTOMATICA" ? "automática" : <>por {h.asignadoPorNombre ?? "—"}</>}
                            </span>
                          </div>
                          {h.motivo && (
                            <div className="mt-1 rounded bg-surface-2 p-1.5 text-fg-muted">{h.motivo}</div>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )
        }
      />

      {modalOpen && a && (
        <AsignarModal
          instanciaId={a.instanciaId}
          estadoCodigo={a.estadoActualCodigo}
          estadoNombre={a.estadoActualNombre}
          sectorCodigo={a.sectorCodigo}
          actual={actual}
          onClose={() => setModalOpen(false)}
        />
      )}

      {avanzarOpen && a && solicitudId && folio && sector && (
        <TransitionsModal
          open={avanzarOpen}
          solicitudId={solicitudId}
          instanciaId={a.instanciaId}
          folio={folio}
          sector={sector}
          onClose={() => setAvanzarOpen(false)}
          onDone={async () => {
            setAvanzarOpen(false);
            await Promise.all([
              qc.invalidateQueries({ queryKey: ["asignacion-instancia", instanciaId] }),
              qc.invalidateQueries({ queryKey: ["asignacion-historial", instanciaId] }),
              qc.invalidateQueries({ queryKey: [`solicitud-${sector}-detalle`, solicitudId] }),
            ]);
          }}
        />
      )}
    </Section>
  );
}

function AsignarModal({
  instanciaId,
  estadoCodigo,
  estadoNombre,
  sectorCodigo,
  actual,
  onClose,
}: {
  instanciaId: string;
  estadoCodigo: string;
  estadoNombre: string | null;
  sectorCodigo: string | null;
  actual: AsignacionEtapa | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [usuarioId, setUsuarioId] = useState("");
  const [motivo, setMotivo] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const esReasignacion = !!actual;

  const qAsignables = useQuery({
    queryKey: ["asignables", sectorCodigo],
    queryFn: async () =>
      (await api.get<UsuarioAsignable[]>(`/api/asignaciones/asignables?sector=${sectorCodigo}`)).data,
    enabled: !!sectorCodigo,
    staleTime: 60_000,
  });

  const submit = async () => {
    if (!usuarioId) { setError("Selecciona al responsable."); return; }
    if (esReasignacion && !motivo.trim()) { setError("La reasignación requiere motivo."); return; }
    setLoading(true);
    setError(null);
    try {
      await api.put(`/api/asignaciones/instancias/${instanciaId}/etapas/${estadoCodigo}`, {
        usuarioId,
        motivo: motivo.trim() || null,
      });
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["asignacion-instancia", instanciaId] }),
        qc.invalidateQueries({ queryKey: ["asignacion-historial", instanciaId] }),
      ]);
      onClose();
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string; title?: string } } }).response?.data?.detail ||
        (e as { response?: { data?: { title?: string } } }).response?.data?.title ||
        (e as Error).message ||
        "Error inesperado.";
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  const candidatos = (qAsignables.data ?? []).filter((u) => u.id !== actual?.usuarioId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-full w-full max-w-lg flex-col rounded-lg bg-surface shadow-overlay">
        <div className="flex shrink-0 items-start justify-between border-b px-5 py-3">
          <div>
            <h2 className="text-base font-semibold text-fg">
              {esReasignacion ? "Reasignar" : "Asignar"} responsable
            </h2>
            <p className="mt-0.5 text-xs text-fg-subtle">
              Etapa <span className="font-medium">{estadoNombre ?? estadoCodigo}</span>
              {actual && <>{" · responsable actual: "}<span className="font-medium">{actual.usuarioNombre}</span></>}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded p-1 text-fg-faint hover:bg-surface-2 hover:text-fg-muted"
            disabled={loading}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-5 py-4">
          <div className="flex items-start gap-2 rounded bg-primary-tint p-2.5 text-xs text-fg-muted">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary-strong" />
            <span>
              Seleccione al responsable interno que dará seguimiento a esta solicitud. Los usuarios
              disponibles se muestran de acuerdo con el sector y los permisos configurados.
            </span>
          </div>

          <label className="block">
            <span className="mb-1 block text-xs font-medium text-fg-muted">Responsable</span>
            <select
              value={usuarioId}
              onChange={(e) => { setUsuarioId(e.target.value); setError(null); }}
              className="w-full rounded border border-edge bg-surface px-3 py-2 text-sm text-fg"
              disabled={loading || qAsignables.isLoading}
            >
              <option value="">
                {qAsignables.isLoading ? "Cargando usuarios…" : "Selecciona un usuario…"}
              </option>
              {candidatos.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.nombreCompleto} ({u.correo})
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1 block text-xs font-medium text-fg-muted">
              Motivo {esReasignacion ? "(obligatorio en reasignación)" : "(opcional)"}
            </span>
            <textarea
              value={motivo}
              onChange={(e) => { setMotivo(e.target.value); setError(null); }}
              rows={3}
              placeholder={esReasignacion ? "P. ej. ausencia del responsable habitual…" : ""}
              className="w-full rounded border border-edge bg-surface px-3 py-2 text-sm text-fg"
              disabled={loading}
            />
          </label>

          {error && (
            <div className="flex items-start gap-2 rounded bg-estado-err-bg p-2 text-xs text-estado-err">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="flex shrink-0 justify-end gap-2 border-t px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="rounded border border-edge px-3 py-1.5 text-sm text-fg-muted transition hover:bg-surface-2"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={loading || !usuarioId}
            className="rounded bg-primary px-3 py-1.5 text-sm font-medium text-white transition hover:bg-primary-hover disabled:opacity-50"
          >
            {loading ? "Guardando…" : esReasignacion ? "Reasignar" : "Asignar"}
          </button>
        </div>
      </div>
    </div>
  );
}
